import { useMemo } from 'react';
import { CalendarIcon } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { SearchFilterConfig } from '@/services/external-chat/types';

interface FilterBarProps {
  filters: SearchFilterConfig[];
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
  fontColor: string;
  borderRadius?: string;
  /** Per-field facet-fetch loading state. Keyed by `filter.field`. */
  loadingByField?: Record<string, boolean>;
  /** Per-field facet-fetch error message. Keyed by `filter.field`. */
  errorByField?: Record<string, string>;
}

// Filter types that render as a dropdown populated by facet values.
// `keyword` is the embed's legacy alias for `select`.
const DROPDOWN_TYPES = new Set(['select', 'multiselect', 'keyword']);
// Filter types that render as a free-form text input.
const TEXT_TYPES = new Set(['text']);
// Filter types that render as a date-range picker. Both the embed's
// native `date_range` and the admin schema's `range` are accepted.
const DATE_TYPES = new Set(['date_range', 'range']);

const toISODate = (d?: Date) => (d ? format(d, 'yyyy-MM-dd') : '');
const fromISODate = (s?: string) => {
  if (!s) return undefined;
  const d = parseISO(s);
  return isValid(d) ? d : undefined;
};

export const FilterBar = ({
  filters,
  values,
  onChange,
  fontColor,
  borderRadius = '8px',
  loadingByField,
  errorByField,
}: FilterBarProps) => {
  if (filters.length === 0) return null;

  const inputStyle = {
    color: fontColor,
    borderColor: fontColor + '25',
    backgroundColor: fontColor + '05',
    borderRadius,
  };

  return (
    <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
      {filters.map((f) => {
        const isDate = DATE_TYPES.has(f.type);
        const isText = TEXT_TYPES.has(f.type);
        const isDropdown = DROPDOWN_TYPES.has(f.type);
        const isLoading = isDropdown && loadingByField?.[f.field] === true;
        const fieldError = isDropdown ? errorByField?.[f.field] : undefined;
        const options = f.options ?? [];
        const isEmpty =
          isDropdown && !isLoading && !fieldError && options.length === 0;
        const placeholder = isLoading
          ? 'Loading…'
          : fieldError
            ? 'Unavailable'
            : isEmpty
              ? 'No values yet'
              : 'All';

        return (
          <div key={f.field} className='flex items-center gap-1.5'>
            {f.label && (
              <span
                className='text-xs font-semibold'
                style={{ color: fontColor }}
              >
                {f.label}:
              </span>
            )}
            {isDate ? (
              <DateRangePicker
                field={f.field}
                label={f.label}
                values={values}
                onChange={onChange}
                fontColor={fontColor}
                inputStyle={inputStyle}
              />
            ) : isText ? (
              <input
                type='text'
                value={values[f.field] || ''}
                onChange={(e) => onChange(f.field, e.target.value)}
                placeholder='All'
                className='border px-2.5 py-1.5 text-xs outline-none'
                style={inputStyle}
                aria-label={f.label || f.field}
              />
            ) : isDropdown ? (
              <select
                value={values[f.field] || ''}
                onChange={(e) => onChange(f.field, e.target.value)}
                disabled={isLoading || !!fieldError || isEmpty}
                className='border px-2.5 py-1.5 text-xs outline-none disabled:opacity-50'
                style={inputStyle}
                aria-label={f.label || f.field}
                title={fieldError || undefined}
              >
                <option value=''>{placeholder}</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

interface DateRangePickerProps {
  field: string;
  label: string;
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
  fontColor: string;
  inputStyle: React.CSSProperties;
}

const DateRangePicker = ({
  field,
  label,
  values,
  onChange,
  fontColor,
  inputStyle,
}: DateRangePickerProps) => {
  const fromStr = values[`${field}_from`] || '';
  const toStr = values[`${field}_to`] || '';

  const selected = useMemo<DateRange | undefined>(() => {
    const from = fromISODate(fromStr);
    const to = fromISODate(toStr);
    if (!from && !to) return undefined;
    return { from, to };
  }, [fromStr, toStr]);

  const buttonText = useMemo(() => {
    const from = fromISODate(fromStr);
    const to = fromISODate(toStr);
    if (from && to)
      return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
    if (from) return `${format(from, 'MMM d, yyyy')} – …`;
    if (to) return `… – ${format(to, 'MMM d, yyyy')}`;
    return 'Pick a date range';
  }, [fromStr, toStr]);

  const handleSelect = (range: DateRange | undefined) => {
    onChange(`${field}_from`, toISODate(range?.from));
    onChange(`${field}_to`, toISODate(range?.to));
  };

  const isEmpty = !fromStr && !toStr;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='flex items-center gap-1.5 border px-2.5 py-1.5 text-xs outline-none'
          style={{
            ...inputStyle,
            color: isEmpty ? fontColor + '80' : fontColor,
          }}
          aria-label={label ? `${label} date range` : 'Date range'}
        >
          <CalendarIcon className='size-3.5 opacity-60' />
          <span>{buttonText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='range'
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={1}
        />
      </PopoverContent>
    </Popover>
  );
};
